import React, {useCallback, useEffect, useRef, useState} from 'react'
import {Stack, TextField, Typography} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'

import {colors} from '@/constants'
import HoverButton from '@/components/UI/button/HoverButton'
import SimpleLoader from '@/components/UI/simpleLoader'
import TransCustom from '@/components/UI/i18next'
import {useTranslation} from 'react-i18next'
import {errorCodeHandler} from '@/helpers/localErrorCodeHandler'
import {isErrorResponse} from '@/helpers/IsErrorResponse'
import SupportService from '@/services/api/Support'
import {
  formatTicketDate,
  isTicketOpen,
  normalizeMessages,
  normalizeTicket,
  SupportMessageItem,
  SupportTicketItem,
} from '@/helpers/supportTickets'
import StatusBadge from './StatusBadge'
import {inputSx, MESSAGE_MAX} from './constants'

interface Props {
  ticket: SupportTicketItem
  onBack: () => void
}

const MessageBubble = ({message}: {message: SupportMessageItem}) => {
  const {t} = useTranslation()
  const {isStaff} = message

  return (
    <Stack alignItems={isStaff ? 'flex-start' : 'flex-end'} width="100%">
      <Stack
        gap="6px"
        padding="12px 14px"
        maxWidth="80%"
        boxSizing="border-box"
        sx={{
          borderRadius: '12px',
          background: isStaff ? colors.brown2 + '80' : '#FF93261A',
          border: `1px solid ${isStaff ? colors.brown2 : colors.orange + '66'}`,
        }}>
        <Stack direction="row" justifyContent="space-between" gap="14px">
          <Typography
            color={isStaff ? colors.yellow : colors.orange}
            fontSize="13px"
            fontWeight="600">
            {message.authorName || (isStaff ? t('1746') : t('1747'))}
          </Typography>
          <Typography color={colors.gray} fontSize="12px" flex="none">
            {formatTicketDate(message.createdAt)}
          </Typography>
        </Stack>
        <Typography
          color={colors.white}
          fontSize="14px"
          lineHeight="150%"
          sx={{whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>
          {message.message}
        </Typography>
      </Stack>
    </Stack>
  )
}

const TicketThread = ({ticket, onBack}: Props) => {
  const {t} = useTranslation()
  const [messages, setMessages] = useState<SupportMessageItem[]>([])
  const [status, setStatus] = useState(ticket.status)
  const [isLoading, setIsLoading] = useState(true)
  const [reply, setReply] = useState('')
  const [busy, setBusy] = useState(false)
  const bottomRef = useRef<HTMLDivElement>(null)

  const load = useCallback(async () => {
    try {
      const response = await SupportService.getTicket(ticket.id)
      if (isErrorResponse(response.data)) {
        errorCodeHandler(response.data)
        return
      }
      setMessages(normalizeMessages(response.data))
      const fresh = normalizeTicket(response.data)
      if (fresh?.status) setStatus(fresh.status)
    } catch (e) {
      errorCodeHandler(e)
    } finally {
      setIsLoading(false)
    }
  }, [ticket.id])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({block: 'end'})
  }, [messages])

  const isOpen = isTicketOpen(status)

  const send = async () => {
    const text = reply.trim()
    if (!text || busy) return
    setBusy(true)
    try {
      const response = await SupportService.reply(ticket.id, text)
      if (isErrorResponse(response.data)) {
        errorCodeHandler(response.data)
        return
      }
      setReply('')
      await load()
    } catch (e) {
      errorCodeHandler(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Stack flex="auto" minHeight={0} gap="14px" marginTop="20px">
      <Stack
        direction="row"
        alignItems="center"
        gap="8px"
        className="pointer"
        width="max-content"
        onClick={onBack}>
        <ArrowBackIcon style={{color: colors.gray, fontSize: '18px'}} />
        <TransCustom i18nKey="1741" color={colors.gray} fontSize="14px" />
      </Stack>

      <Stack direction="row" alignItems="center" gap="12px" flexWrap="wrap">
        <Typography color={colors.white} fontSize="20px" fontWeight="600">
          {ticket.subject}
        </Typography>
        <StatusBadge status={status} />
      </Stack>

      <Stack
        className="events-block"
        flex="auto"
        minHeight={0}
        overflow="auto"
        gap="14px"
        padding="14px 16px 14px 0"
        boxSizing="border-box"
        borderTop={`1px solid ${colors.lightBrown}40`}
        borderBottom={`1px solid ${colors.lightBrown}40`}
        sx={{scrollbarGutter: 'stable'}}>
        {isLoading ? (
          <SimpleLoader isLoad={true} local={true} />
        ) : messages.length === 0 ? (
          <TransCustom
            i18nKey="1750"
            color={colors.gray}
            fontSize="14px"
            textAlign="center"
          />
        ) : (
          <>
            {messages.map((item) => (
              <MessageBubble key={item.id} message={item} />
            ))}
            <div ref={bottomRef} />
          </>
        )}
      </Stack>

      {isOpen ? (
        <Stack direction="row" gap="12px" alignItems="flex-end" flex="none">
          <TextField
            value={reply}
            onChange={(e) => setReply(e.target.value.slice(0, MESSAGE_MAX))}
            placeholder={String(t('1742'))}
            multiline
            minRows={2}
            maxRows={5}
            fullWidth
            sx={inputSx}
          />
          <HoverButton
            onClick={send}
            disabled={!reply.trim() || busy}
            fontSize="15px"
            fontWeight="400"
            height="42px"
            padding="10px 32px"
            flex="none"
            textColor={colors.brown}
            background={colors.yellow}
            style={{whiteSpace: 'nowrap'}}>
            {t('1743')}
          </HoverButton>
        </Stack>
      ) : (
        <TransCustom i18nKey="1748" color={colors.gray} fontSize="13px" />
      )}
    </Stack>
  )
}

export default TicketThread
